import React, { useEffect, useRef, useState } from "react";
import { api, imageProxyUrl, fileUrl } from "@/lib/api";
import { useBuilder } from "@/lib/builderStore";
import { Switch } from "../components/ui/switch";
import { PdfPreview } from "../components/PdfPreview";
import { BuilderCard, SubLabel } from "../components/BuilderCard";
import { useIsMobile } from "@/lib/useIsMobile";
import { toast } from "sonner";
import {
  Upload,
  Sparkles,
  Download,
  FileSpreadsheet,
  AlertTriangle,
  Loader2,
  Image as ImageIcon,
  DollarSign,
  Eye,
  EyeOff,
  Type,
  RefreshCw,
  Languages,
  LayoutGrid,
  Building2,
  Camera,
  FolderDown,
  X as XIcon,
} from "lucide-react";

export default function CatalogBuilder() {
  const [config, setConfig] = useState(null);
  const [presets, setPresets] = useState([]);
  const { parse, setParse, job, setJob, lastJob, setLastJob, lastMediaJob, setLastMediaJob, resetBuilder } = useBuilder();
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState(null); // { stage, progress_pct, progress_current, progress_total }
  const [previewDirty, setPreviewDirty] = useState(true);
  // Media ZIP download state
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [mediaIncludePhotos, setMediaIncludePhotos] = useState(true);
  const [mediaIncludeVideos, setMediaIncludeVideos] = useState(true);
  const [mediaGenerating, setMediaGenerating] = useState(false);
  const [mediaStatus, setMediaStatus] = useState(null); // { stage, progress_pct, progress_current, progress_total }
  // Hold onto the last uploaded File object so switching supplier can re-parse
  // the same bytes without the user having to re-drop it.
  const [lastUpload, setLastUpload] = useState(null);
  // Flagged rows need an explicit "Continue anyway" before generating
  const [reviewAck, setReviewAck] = useState(false);
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState("settings");
  const [openCards, setOpenCards] = useState({});
  useEffect(() => {
    // Desktop: everything open (Branding folded when a preset sets it). Mobile: only Data.
    setOpenCards(isMobile ? { data: true } : { data: true, layout: true, branding: !job.preset_id, pricing: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);
  const toggleCard = (id) =>
    setOpenCards((o) => (isMobile ? { [id]: !o[id] } : { ...o, [id]: !o[id] }));
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  // Snapshot of the job the last preview was rendered from — used to compute
  // `dirty` (any subsequent change to `job` makes the Preview button live).
  const lastPreviewJobRef = useRef(null);

  const activePreset = presets.find((p) => p.id === job.preset_id) || null;
  const suppliers = config?.suppliers || [];
  const activeSupplier = suppliers.find((s) => s.code === parse?.supplier) || null;
  const hasConcepts = !!activeSupplier?.has_concepts;
  const hasPriceSplit = activeSupplier ? activeSupplier.has_price_split : true;

  // Recompute preview-dirty whenever the job config changes.
  useEffect(() => {
    if (!lastPreviewJobRef.current) {
      setPreviewDirty(true);
      return;
    }
    setPreviewDirty(JSON.stringify(job) !== lastPreviewJobRef.current);
  }, [job]);

  // When a new file is parsed, ensure the selected price_source is actually
  // present in that file — otherwise fall through to the first available one.
  useEffect(() => {
    const avail = parse?.available_prices || [];
    if (!avail.length) return;
    const codeToField = { cost: "cost_price", regular: "total_sale", website: "website_price" };
    const currentField = codeToField[job.price_source || "regular"];
    if (!avail.includes(currentField)) {
      const firstAvail = ["regular", "website", "cost"].find((c) => avail.includes(codeToField[c])) || "regular";
      setJob((j) => ({ ...j, price_source: firstAvail }));
    }
  }, [parse?.available_prices, job.price_source, setJob]);

  const requestPreview = () => {
    lastPreviewJobRef.current = JSON.stringify(job);
    setPreviewDirty(false);
    window.dispatchEvent(new CustomEvent("catalog:preview-refresh"));
  };

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/presets").then((r) => setPresets(r.data)).catch(() => {});
  }, []);

  const onExcelChosen = async (file, supplierHint = null) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (supplierHint) fd.append("supplier", supplierHint);
    setUploading(true);
    try {
      const { data } = await api.post("/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParse(data);
      setReviewAck(false);
      setLastUpload(file);
      setLastJob(null);
      setLastMediaJob(null);
      // Restore last-used concept for Daniela (per-supplier, browser-local)
      let nextConcept = null;
      const supplierMeta = (config?.suppliers || []).find((s) => s.code === data.supplier);
      if (supplierMeta?.has_concepts) {
        try {
          const stored = localStorage.getItem(`jc-concept-${data.supplier}`);
          const validCodes = (supplierMeta.concepts || []).map((c) => c.code);
          nextConcept = validCodes.includes(stored) ? stored : (validCodes[0] || null);
        } catch (_) {
          nextConcept = (supplierMeta.concepts?.[0]?.code) || null;
        }
      }
      setJob((j) => ({
        ...j,
        parse_id: data.id,
        concept: nextConcept,
        overrides: { ...j.overrides, name: (data.filename || "Catalog").replace(/\.[^.]+$/, "") },
      }));
      toast.success(`Parsed ${data.item_count} items · ${supplierMeta?.label || data.supplier}`, {
        description: data.warnings?.length ? `${data.warnings.length} warnings` : "No warnings",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to parse file");
    } finally {
      setUploading(false);
    }
  };

  // Re-parse the last uploaded file under a different supplier.
  const changeSupplier = async (code) => {
    if (!code || !lastUpload) return;
    await onExcelChosen(lastUpload, code);
  };

  const loadSample = async () => {
    setUploading(true);
    try {
      const { data } = await api.get("/parse/sample");
      setParse(data);
      setLastUpload(null); // sample has no local File — supplier switch disabled
      setLastJob(null);
      setLastMediaJob(null);
      setJob((j) => ({
        ...j,
        parse_id: data.id,
        concept: null,
        overrides: { ...j.overrides, name: (data.filename || "Sample").replace(/\.[^.]+$/, "") },
      }));
      toast.success(`Loaded sample · ${data.item_count} items`, {
        description: data.warnings?.length ? `${data.warnings.length} warnings` : "No warnings",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load sample");
    } finally {
      setUploading(false);
    }
  };

  const onLogoChosen = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "logos");
    setUploadingLogo(true);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob((j) => ({ ...j, overrides: { ...j.overrides, logo_file_id: data.id } }));
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const applyPreset = (presetId) => {
    if (!presetId) {
      setJob((j) => ({ ...j, preset_id: null }));
      return;
    }
    const p = presets.find((x) => x.id === presetId);
    if (!p) return;
    setJob((j) => ({
      ...j,
      preset_id: p.id,
      show_price: p.show_price_default,
      price_rule: p.default_price_rule || j.price_rule,
      overrides: {
        ...j.overrides,
        heading_font: p.heading_font,
        body_font: p.body_font,
        primary_color: p.primary_color,
        accent_color: p.accent_color,
        background_color: p.background_color,
        page_size: p.page_size,
        logo_file_id: p.logo_file_id,
        tagline: p.tagline,
        name: p.name || j.overrides.name,
      },
    }));
    toast.info(`Preset "${p.name}" loaded`);
  };

  const generate = async () => {
    if (!parse) return;
    setGenerating(true);
    setGenStatus({ stage: "queued", progress_pct: 0, progress_current: 0, progress_total: 0 });
    try {
      const { data: kickoff } = await api.post("/generate", job);
      const jobId = kickoff.id;

      // Poll for completion
      const started = Date.now();
      const MAX_MS = 10 * 60 * 1000; // 10 minutes hard cap
      let finalDoc = null;
      while (true) {
        await new Promise((r) => setTimeout(r, 1500));
        const { data: doc } = await api.get(`/jobs/${jobId}`);
        setGenStatus({
          stage: doc.stage,
          progress_pct: doc.progress_pct ?? 0,
          progress_current: doc.progress_current ?? 0,
          progress_total: doc.progress_total ?? 0,
        });
        if (doc.status === "completed") {
          finalDoc = doc;
          break;
        }
        if (doc.status === "failed") {
          throw new Error(doc.error || "Generation failed");
        }
        if (Date.now() - started > MAX_MS) {
          throw new Error("Generation timed out after 10 minutes");
        }
      }

      setLastJob({
        id: finalDoc.id,
        pdf_file_id: finalDoc.pdf_file_id,
        expires_at: finalDoc.expires_at,
        item_count: finalDoc.item_count,
        filename: finalDoc.filename,
        is_bundled_sample: finalDoc.is_bundled_sample,
      });
      toast.success("Catalog PDF generated", {
        description: `Expires ${new Date(finalDoc.expires_at).toLocaleDateString()}`,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Generation failed");
    } finally {
      setGenerating(false);
      setGenStatus(null);
    }
  };

  const stageLabel = (s) => {
    switch (s?.stage) {
      case "queued":
        return "Preparing…";
      case "downloading_images":
        return s.progress_total > 0
          ? `Downloading images ${s.progress_current}/${s.progress_total}`
          : "Downloading images…";
      case "preparing_media":
        return s.progress_total > 0
          ? `Preparing video & certificate tiles ${s.progress_current}/${s.progress_total}`
          : "Preparing video & certificate tiles…";
      case "rendering_pdf":
        return "Rendering PDF…";
      case "uploading":
        return "Uploading…";
      case "completed":
        return "Done";
      case "failed":
        return "Failed";
      default:
        return "Working…";
    }
  };

  const mediaStageLabel = (s) => {
    switch (s?.stage) {
      case "queued":
        return "Preparing…";
      case "downloading_media":
        return s.progress_total > 0
          ? `Downloading media ${s.progress_current}/${s.progress_total}`
          : "Downloading media…";
      case "zipping":
        return "Building ZIP…";
      case "uploading":
        return "Uploading…";
      case "completed":
        return "Done";
      case "failed":
        return "Failed";
      default:
        return "Working…";
    }
  };

  const startMediaDownload = async () => {
    if (!parse) return;
    if (!mediaIncludePhotos && !mediaIncludeVideos && !job.show_cert_tiles) return;
    setMediaDialogOpen(false);
    setMediaGenerating(true);
    setMediaStatus({ stage: "queued", progress_pct: 0, progress_current: 0, progress_total: 0 });
    try {
      const { data: kickoff } = await api.post("/media-zip", {
        parse_id: parse.id,
        include_photos: mediaIncludePhotos,
        include_videos: mediaIncludeVideos,
        include_certificates: !!job.show_cert_tiles,
        concept: job.concept || null,
        name: job.overrides?.name || null,
      });
      const jobId = kickoff.id;
      const started = Date.now();
      const MAX_MS = 15 * 60 * 1000; // 15 minutes hard cap
      let finalDoc = null;
      let transientErrors = 0;
      while (true) {
        await new Promise((r) => setTimeout(r, 1500));
        let doc;
        try {
          const resp = await api.get(`/media-jobs/${jobId}`);
          doc = resp.data;
          transientErrors = 0;
        } catch (err) {
          // Tolerate up to 10 consecutive transient errors (network blip, 502,
          // backend reload) so the user doesn't lose a long-running ZIP build.
          transientErrors += 1;
          if (transientErrors > 10) {
            throw err;
          }
          continue;
        }
        setMediaStatus({
          stage: doc.stage,
          progress_pct: doc.progress_pct ?? 0,
          progress_current: doc.progress_current ?? 0,
          progress_total: doc.progress_total ?? 0,
        });
        if (doc.status === "completed") {
          finalDoc = doc;
          break;
        }
        if (doc.status === "failed") {
          throw new Error(doc.error || "Media download failed");
        }
        if (Date.now() - started > MAX_MS) {
          throw new Error("Media download timed out after 15 minutes");
        }
      }
      setLastMediaJob({
        id: finalDoc.id,
        zip_file_id: finalDoc.zip_file_id,
        filename: finalDoc.filename,
        expires_at: finalDoc.expires_at,
        missing_count: finalDoc.missing_count || 0,
        zip_size: finalDoc.zip_size || 0,
      });
      const descBits = [];
      if (finalDoc.missing_count) descBits.push(`${finalDoc.missing_count} file(s) skipped`);
      descBits.push(`Expires ${new Date(finalDoc.expires_at).toLocaleDateString()}`);
      toast.success("Media ZIP ready", { description: descBits.join(" · ") });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Media download failed");
    } finally {
      setMediaGenerating(false);
      setMediaStatus(null);
    }
  };

  const items = parse?.items || [];
  const warnings = parse?.warnings || [];
  const reviewPending = !!parse?.needs_review && !reviewAck;

  const layoutMeta = (config?.layouts || []).find((l) => l.code === job.layout);
  const selectedLayoutDescription = layoutMeta
    ? `${layoutMeta.label} — ${layoutMeta.description}`
    : `Preset default${activePreset?.default_layout ? ` (${activePreset.default_layout.replace("_", " ")})` : ""} — uses whatever the preset is set to`;
  const langLabel = (config?.languages || []).find((l) => l.code === (job.language || "en"))?.label || "English";
  const dataSummary = parse
    ? `${parse.filename} · ${parse.item_count} items${activeSupplier ? ` · ${activeSupplier.label}` : ""}`
    : "No file yet";
  const layoutSummary = `${layoutMeta?.label || "Preset default"} · ${job.overrides.page_size} · ${langLabel}${
    job.show_cover === false ? " · No cover" : ""
  }`;
  const brandingSummary = `${activePreset?.name || "Custom"}${job.overrides.name ? ` · ${job.overrides.name}` : ""}`;
  const pricingSummary =
    !job.show_price || job.price_rule.mode === "hide"
      ? "Prices hidden"
      : job.price_rule.mode === "adjust"
      ? `Adjusted ${job.price_rule.value > 0 ? "+" : ""}${job.price_rule.value}${job.price_rule.type === "percent" ? "%" : ""} · ${job.currency}`
      : `Original prices · ${job.currency}`;

  return (
    <div
      className="max-w-[1600px] mx-auto lg:grid lg:grid-cols-[minmax(460px,540px)_1fr] lg:gap-8 px-4 pt-4 pb-32 lg:p-8"
      data-testid="builder-root"
    >
      {/* Mobile: Settings / Preview switch */}
      <div className="lg:hidden sticky top-[57px] z-20 -mx-4 px-4 py-2 bg-[var(--brand-bg)]/95 backdrop-blur-sm mb-3">
        <div className="grid grid-cols-2 p-1 border hairline rounded-sm bg-white" data-testid="mobile-view-switch">
          {["settings", "preview"].map((v) => (
            <button
              key={v}
              type="button"
              data-testid={`mobile-view-${v}`}
              onClick={() => setMobileView(v)}
              className={`relative py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors ${
                mobileView === v ? "bg-neutral-900 text-white" : "text-neutral-600"
              }`}
            >
              {v === "settings" ? "Settings" : "Preview"}
              {v === "preview" && previewDirty && parse && (
                <span data-testid="mobile-preview-dot" className="absolute top-2 right-3 w-1.5 h-1.5 bg-amber-500 rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* LEFT: Settings */}
      <aside className={`${mobileView === "settings" ? "block" : "hidden"} lg:block space-y-4 lg:pb-0 animate-[fadeIn_.2s_ease-out]`} data-testid="settings-panel">
        <BuilderCard
          id="data"
          title="Data"
          icon={<FileSpreadsheet className="w-3.5 h-3.5" />}
          summary={dataSummary}
          open={!!openCards.data}
          onToggle={() => toggleCard("data")}
        >
          <div
            data-testid="excel-drop-zone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) onExcelChosen(f);
            }}
            className="border border-dashed hairline rounded-sm bg-white p-6 flex flex-col items-center text-center gap-3 hover-lift cursor-pointer"
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              data-testid="excel-file-input"
              onChange={(e) => {
                onExcelChosen(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 text-neutral-500" />}
            <div>
              <div className="text-sm font-medium">{parse ? parse.filename : "Drop .xlsx or .csv here"}</div>
              <div className="text-[11px] text-neutral-500 mt-1">
                {parse ? `${parse.item_count} items · ${warnings.length} warnings` : "or click to browse"}
              </div>
            </div>
          </div>

          {config?.sample_available && (
            <button
              data-testid="load-sample-btn"
              type="button"
              onClick={loadSample}
              disabled={uploading}
              className="mt-2 w-full text-[11px] uppercase tracking-widest text-neutral-600 hover:text-neutral-900 flex items-center justify-center gap-2 py-2 border hairline rounded-sm bg-white hover:bg-neutral-50 disabled:opacity-40"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              {parse?.is_bundled_sample ? "Reload Sample Catalog" : "Use Sample Catalog"}
            </button>
          )}

          {parse && (
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Building2 className="w-3 h-3 text-neutral-500" />
                <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                  Supplier Format
                </span>
                {parse.supplier_auto_detected && (
                  <span
                    data-testid="supplier-auto-tag"
                    className="text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm"
                  >
                    Auto-detected
                  </span>
                )}
              </div>
              <select
                data-testid="supplier-select"
                value={parse.supplier || ""}
                disabled={!lastUpload || uploading}
                onChange={(e) => changeSupplier(e.target.value)}
                className="w-full border hairline rounded-sm bg-white text-sm px-3 py-2 disabled:bg-neutral-50 disabled:text-neutral-500"
                title={!lastUpload ? "Re-upload a file to switch supplier" : ""}
              >
                {suppliers.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.label}
                  </option>
                ))}
              </select>
              {!lastUpload && (
                <div className="text-[10px] text-neutral-400 mt-1">
                  Re-upload the file to switch supplier
                </div>
              )}
            </div>
          )}

          {hasConcepts && (
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Camera className="w-3 h-3 text-neutral-500" />
                <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                  Concept
                </span>
              </div>
              <select
                data-testid="concept-select"
                value={job.concept || activeSupplier?.concepts?.[0]?.code || "1"}
                onChange={(e) => {
                  const code = e.target.value;
                  setJob((j) => ({ ...j, concept: code }));
                  try {
                    localStorage.setItem(`jc-concept-${parse.supplier}`, code);
                  } catch (_) { /* noop */ }
                }}
                className="w-full border hairline rounded-sm bg-white text-sm px-3 py-2"
              >
                {(activeSupplier?.concepts || []).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-neutral-400 mt-1">
                Items without media in this concept render text-only.
              </div>
            </div>
          )}

          {warnings.length > 0 && (
            <div
              data-testid="warnings-summary"
              className="mt-3 rounded-sm border border-[var(--brand-warn-border)] bg-[var(--brand-warn-bg)] px-4 py-3 text-[12px] text-[var(--brand-warn-text)]"
            >
              <div className="flex items-center gap-2 mb-2 font-semibold">
                <AlertTriangle className="w-3.5 h-3.5" /> {warnings.length} validation warning
                {warnings.length > 1 ? "s" : ""}
              </div>
              <ul className="space-y-1 max-h-32 overflow-auto">
                {warnings.slice(0, 20).map((w, i) => (
                  <li key={i}>
                    <span className="opacity-60">Row {w.row}:</span> {w.message}
                  </li>
                ))}
                {warnings.length > 20 && (
                  <li className="opacity-60">+ {warnings.length - 20} more…</li>
                )}
              </ul>
              {reviewPending && (
                <ReviewChoice
                  onFix={() => fileRef.current?.click()}
                  onContinue={() => setReviewAck(true)}
                />
              )}
            </div>
          )}
        </BuilderCard>

        <BuilderCard
          id="layout"
          title="Layout & Page"
          icon={<LayoutGrid className="w-3.5 h-3.5" />}
          summary={layoutSummary}
          open={!!openCards.layout}
          onToggle={() => toggleCard("layout")}
        >
          <div>
            <SubLabel>{!job.layout ? "Layout · preset default" : "Layout"}</SubLabel>
            <div className="grid grid-cols-3 gap-1.5" data-testid="layout-grid">
              <LayoutTile
                testid="layout-preset-default"
                active={!job.layout}
                label="Preset"
                icon={<span className="text-[11px] text-neutral-500">◇</span>}
                onClick={() => setJob((j) => ({ ...j, layout: null }))}
              />
              {(config?.layouts || []).map((l) => (
                <LayoutTile
                  key={l.code}
                  testid={`layout-${l.code}`}
                  active={job.layout === l.code}
                  label={l.label.replace("Grid · 6 per page", "Grid 6")}
                  icon={<LayoutIcon code={l.code} />}
                  onClick={() => setJob((j) => ({ ...j, layout: l.code }))}
                />
              ))}
            </div>
            <div data-testid="layout-description" className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
              {selectedLayoutDescription}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4">
            <div>
              <SubLabel>Page Size</SubLabel>
              <div className="flex gap-1.5">
                {(config?.page_sizes || ["A4", "Letter", "A3", "A5"]).map((sz) => (
                  <ChoiceBtn
                    key={sz}
                    testid={`page-size-${sz}`}
                    active={job.overrides.page_size === sz}
                    onClick={() => setJob((j) => ({ ...j, overrides: { ...j.overrides, page_size: sz } }))}
                  >
                    {sz}
                  </ChoiceBtn>
                ))}
              </div>
            </div>
            <div>
              <SubLabel icon={<Languages className="w-3 h-3" />}>Language</SubLabel>
              <div className="flex gap-1.5">
                {(config?.languages || [{ code: "en", label: "English" }, { code: "ru", label: "Русский" }]).map((lng) => (
                  <ChoiceBtn
                    key={lng.code}
                    testid={`language-${lng.code}`}
                    active={(job.language || "en") === lng.code}
                    onClick={() => setJob((j) => ({ ...j, language: lng.code }))}
                  >
                    {lng.code.toUpperCase()}
                  </ChoiceBtn>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2.5 pt-1">
            <TileToggle
              testId="toggle-cover-page"
              label="Cover page"
              hint="Logo, catalog name and tagline on the first page. Turn off to start straight with the items"
              checked={job.show_cover !== false}
              onChange={(v) => setJob((j) => ({ ...j, show_cover: v }))}
            />
            <TileToggle
              testId="toggle-video-tiles"
              label="Show video as image"
              hint="A still frame with a play icon in the gallery, instead of a Video badge"
              checked={job.show_video_tiles !== false}
              onChange={(v) => setJob((j) => ({ ...j, show_video_tiles: v }))}
            />
            <TileToggle
              testId="toggle-cert-tiles"
              label="Show certificate as image"
              hint="First page of the certificate in the gallery; also adds certificates to the media ZIP"
              checked={!!job.show_cert_tiles}
              onChange={(v) => setJob((j) => ({ ...j, show_cert_tiles: v }))}
            />
            <div className="text-[10px] text-neutral-400">Gallery tiles appear in Editorial and Photo Story layouts.</div>
          </div>
        </BuilderCard>

        <BuilderCard
          id="branding"
          title="Branding"
          icon={<Sparkles className="w-3.5 h-3.5" />}
          summary={brandingSummary}
          open={!!openCards.branding}
          onToggle={() => toggleCard("branding")}
        >
          <Field label="Preset · shared across the team">
          <select
            data-testid="preset-select"
            className="w-full border hairline rounded-sm bg-white text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-neutral-900"
            value={job.preset_id || ""}
            onChange={(e) => applyPreset(e.target.value)}
          >
            <option value="">— Custom / no preset —</option>
            {presets.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          </Field>
          <Field label="Catalog Name">
            <input
              data-testid="input-catalog-name"
              value={job.overrides.name}
              onChange={(e) => setJob((j) => ({ ...j, overrides: { ...j.overrides, name: e.target.value } }))}
              className="w-full border hairline rounded-sm bg-white text-sm px-3 py-2"
            />
          </Field>
          <Field label="Tagline">
            <input
              data-testid="input-tagline"
              value={job.overrides.tagline}
              onChange={(e) => setJob((j) => ({ ...j, overrides: { ...j.overrides, tagline: e.target.value } }))}
              className="w-full border hairline rounded-sm bg-white text-sm px-3 py-2"
              placeholder="Curated Collection"
            />
          </Field>

          <Field label="Logo">
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                ref={logoRef}
                className="hidden"
                data-testid="logo-file-input"
                onChange={(e) => onLogoChosen(e.target.files?.[0])}
              />
              <button
                data-testid="upload-logo-btn"
                onClick={() => logoRef.current?.click()}
                className="text-xs uppercase tracking-widest px-3 py-2 border hairline rounded-sm bg-white hover:bg-neutral-50 flex items-center gap-2"
              >
                {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                {job.overrides.logo_file_id ? "Replace Logo" : "Upload Logo"}
              </button>
              {job.overrides.logo_file_id && (
                <img
                  src={fileUrl(job.overrides.logo_file_id)}
                  alt="logo"
                  className="h-10 max-w-24 object-contain"
                  data-testid="logo-preview"
                />
              )}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Heading Font">
              <select
                data-testid="select-heading-font"
                value={job.overrides.heading_font}
                onChange={(e) => setJob((j) => ({ ...j, overrides: { ...j.overrides, heading_font: e.target.value } }))}
                className="w-full border hairline rounded-sm bg-white text-sm px-2 py-2"
              >
                {(config?.fonts?.heading || ["Fraunces"]).map((f) => <option key={f}>{f}</option>)}
              </select>
            </Field>
            <Field label="Body Font">
              <select
                data-testid="select-body-font"
                value={job.overrides.body_font}
                onChange={(e) => setJob((j) => ({ ...j, overrides: { ...j.overrides, body_font: e.target.value } }))}
                className="w-full border hairline rounded-sm bg-white text-sm px-2 py-2"
              >
                {(config?.fonts?.body || ["Manrope"]).map((f) => <option key={f}>{f}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            <ColorField label="Primary" testid="color-primary"
              value={job.overrides.primary_color}
              onChange={(v) => setJob((j) => ({ ...j, overrides: { ...j.overrides, primary_color: v } }))}
            />
            <ColorField label="Accent" testid="color-accent"
              value={job.overrides.accent_color}
              onChange={(v) => setJob((j) => ({ ...j, overrides: { ...j.overrides, accent_color: v } }))}
            />
            <ColorField label="Background" testid="color-background"
              value={job.overrides.background_color}
              onChange={(v) => setJob((j) => ({ ...j, overrides: { ...j.overrides, background_color: v } }))}
            />
          </div>
        </BuilderCard>

        <BuilderCard
          id="pricing"
          title="Pricing"
          icon={<DollarSign className="w-3.5 h-3.5" />}
          summary={pricingSummary}
          open={!!openCards.pricing}
          onToggle={() => toggleCard("pricing")}
        >
          {hasPriceSplit && parse?.available_prices && parse.available_prices.length > 1 && (
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                Price Source
              </div>
              <div className="flex gap-1.5" data-testid="price-source-group">
                {[
                  { code: "cost", field: "cost_price", label: "Cost" },
                  { code: "regular", field: "total_sale", label: "Regular" },
                  { code: "website", field: "website_price", label: "Website" },
                ].map((s) => {
                  const available = parse.available_prices.includes(s.field);
                  return (
                    <button
                      key={s.code}
                      data-testid={`price-source-${s.code}`}
                      disabled={!available}
                      onClick={() => setJob((j) => ({ ...j, price_source: s.code }))}
                      className={`flex-1 px-2 py-1.5 text-[11px] uppercase tracking-widest rounded-sm border ${
                        (job.price_source || "regular") === s.code
                          ? "bg-neutral-900 text-white border-neutral-900"
                          : available
                          ? "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-400"
                          : "bg-neutral-50 border-neutral-100 text-neutral-300 cursor-not-allowed"
                      }`}
                      title={available ? "" : "Column not present in this file"}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex gap-2 mb-3">
            <ToggleChip
              testid="price-mode-hide"
              active={!job.show_price || job.price_rule.mode === "hide"}
              onClick={() => setJob((j) => ({ ...j, show_price: false, price_rule: { ...j.price_rule, mode: "hide" } }))}
              icon={<EyeOff className="w-3 h-3" />}
            >
              Hide
            </ToggleChip>
            <ToggleChip
              testid="price-mode-original"
              active={job.show_price && job.price_rule.mode === "original"}
              onClick={() => setJob((j) => ({ ...j, show_price: true, price_rule: { ...j.price_rule, mode: "original" } }))}
              icon={<Eye className="w-3 h-3" />}
            >
              Original
            </ToggleChip>
            <ToggleChip
              testid="price-mode-adjust"
              active={job.show_price && job.price_rule.mode === "adjust"}
              onClick={() => setJob((j) => ({ ...j, show_price: true, price_rule: { ...j.price_rule, mode: "adjust" } }))}
            >
              Adjust
            </ToggleChip>
          </div>
          {job.show_price && job.price_rule.mode === "adjust" && (
            <div className="grid grid-cols-[100px_1fr_80px] gap-2 items-center">
              <select
                data-testid="price-adjust-type"
                value={job.price_rule.type}
                onChange={(e) => setJob((j) => ({ ...j, price_rule: { ...j.price_rule, type: e.target.value } }))}
                className="border hairline rounded-sm bg-white text-sm px-2 py-2"
              >
                <option value="percent">Percent</option>
                <option value="fixed">Fixed</option>
              </select>
              <input
                data-testid="price-adjust-value"
                type="number"
                value={job.price_rule.value}
                onChange={(e) => setJob((j) => ({ ...j, price_rule: { ...j.price_rule, value: parseFloat(e.target.value || "0") } }))}
                className="border hairline rounded-sm bg-white text-sm px-3 py-2"
              />
              <input
                data-testid="input-currency"
                value={job.currency}
                onChange={(e) => setJob((j) => ({ ...j, currency: e.target.value }))}
                className="border hairline rounded-sm bg-white text-sm px-3 py-2 text-center"
              />
            </div>
          )}
        </BuilderCard>

        {/* Desktop action bar */}
        <div className="hidden lg:block sticky bottom-0 pt-3 pb-3 bg-[var(--brand-bg)] space-y-2 z-10" data-testid="action-bar">
          <div className="grid grid-cols-[1fr_1.4fr] gap-2">
          <button
            data-testid="preview-refresh-btn"
            disabled={!parse || !previewDirty}
            onClick={requestPreview}
            className="w-full flex items-center justify-center gap-2 border border-neutral-900 text-neutral-900 text-sm font-medium py-2.5 rounded-sm disabled:opacity-30 disabled:border-neutral-300 hover:bg-neutral-900 hover:text-white transition-all press-in"
          >
            <Eye className="w-4 h-4" />
            {previewDirty ? "Refresh Preview" : "Preview Up-to-date"}
            {previewDirty && (
              <span className="ml-1 w-1.5 h-1.5 bg-amber-500 rounded-full" />
            )}
          </button>
          <button
            data-testid="generate-pdf-btn"
            disabled={!parse || generating || reviewPending}
            title={reviewPending ? "Review the flagged rows first" : ""}
            onClick={generate}
            className="w-full flex items-center justify-center gap-2 bg-neutral-900 text-white text-sm font-medium py-3.5 rounded-sm disabled:opacity-40 hover:bg-black transition-all press-in"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Generate PDF
          </button>
          </div>
          {generating && genStatus && (
            <div
              data-testid="generation-progress"
              className="border hairline bg-white rounded-sm px-4 py-3 text-xs space-y-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">{stageLabel(genStatus)}</span>
                <span
                  data-testid="generation-progress-pct"
                  className="tabular-nums text-neutral-500"
                >
                  {genStatus.progress_pct ?? 0}%
                </span>
              </div>
              <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-neutral-900 transition-[width] duration-500"
                  style={{ width: `${genStatus.progress_pct ?? 0}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Media download dialog */}
      {mediaDialogOpen && (
        <div
          data-testid="media-dialog-overlay"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 sm:p-4"
          onClick={() => setMediaDialogOpen(false)}
        >
          <div
            data-testid="media-dialog"
            className="relative bg-white border hairline rounded-t-xl sm:rounded-sm shadow-xl w-full sm:max-w-sm p-6 pb-8 sm:pb-6 animate-[sheetUp_.25s_ease-out]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setMediaDialogOpen(false)}
              className="absolute top-3 right-3 text-neutral-500 hover:text-neutral-900"
              aria-label="Close"
              data-testid="media-dialog-close"
            >
              <XIcon className="w-4 h-4" />
            </button>
            <div className="overline mb-1">Download Media</div>
            <h2 className="font-serif-editorial text-xl mb-4">What to include?</h2>
            <p className="text-xs text-neutral-500 mb-5">
              Files are grouped in one ZIP, named by SKU. Mirrors the generated catalog exactly.
            </p>

            <div className="space-y-3 text-sm">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="media-include-photos"
                  checked={mediaIncludePhotos}
                  onChange={(e) => setMediaIncludePhotos(e.target.checked)}
                  className="w-4 h-4"
                />
                <span>Photos</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="media-include-videos"
                  checked={mediaIncludeVideos}
                  onChange={(e) => setMediaIncludeVideos(e.target.checked)}
                  className="w-4 h-4"
                />
                <span>Videos</span>
              </label>
            </div>
            {job.show_cert_tiles && (
              <div data-testid="media-cert-note" className="mt-3 text-[11px] text-neutral-500">
                Certificates are included too (Show certificate as image is on).
              </div>
            )}

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => setMediaDialogOpen(false)}
                data-testid="media-dialog-cancel"
                className="text-xs uppercase tracking-widest px-4 py-3 sm:py-2 border border-neutral-300 rounded-sm text-neutral-600 hover:bg-neutral-50"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="media-dialog-confirm"
                disabled={!mediaIncludePhotos && !mediaIncludeVideos && !job.show_cert_tiles}
                onClick={startMediaDownload}
                className="text-xs uppercase tracking-widest px-4 py-3 sm:py-2 bg-neutral-900 text-white rounded-sm hover:bg-black disabled:opacity-40 flex items-center justify-center gap-2"
              >
                <FolderDown className="w-3.5 h-3.5" />
                Build ZIP
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RIGHT: Preview */}
      <section
        className={`${mobileView === "preview" ? "block" : "hidden"} lg:block min-h-[70vh] min-w-0`}
        data-testid="preview-panel"
      >
        <div className="mx-auto w-full lg:max-w-[680px] lg:sticky lg:top-[84px]">
        {parse && (
          <OutputStrip
            lastJob={lastJob}
            lastMediaJob={lastMediaJob}
            mediaGenerating={mediaGenerating}
            mediaStatus={mediaStatus}
            mediaStageLabel={mediaStageLabel}
            mediaDisabled={!parse || mediaGenerating || reviewPending}
            onMedia={() => setMediaDialogOpen(true)}
            mobileProgress={generating && genStatus ? (
              <div className="lg:hidden">
          {generating && genStatus && (
            <div
              data-testid="mobile-generation-progress"
              className="border hairline bg-white rounded-sm px-4 py-3 text-xs space-y-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">{stageLabel(genStatus)}</span>
                <span
                  data-testid="mobile-generation-progress-pct"
                  className="tabular-nums text-neutral-500"
                >
                  {genStatus.progress_pct ?? 0}%
                </span>
              </div>
              <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-neutral-900 transition-[width] duration-500"
                  style={{ width: `${genStatus.progress_pct ?? 0}%` }}
                />
              </div>
            </div>
          )}
              </div>
            ) : null}
          />
        )}
        <CatalogPreview
          items={items}
          job={job}
          presets={presets}
          dirty={previewDirty}
          isMobile={isMobile}
          title={job.overrides.name || "Untitled Catalog"}
          onReset={() => {
            if (window.confirm("Reset the current catalog and start fresh?")) resetBuilder();
          }}
          onPreviewLoaded={() => {
            lastPreviewJobRef.current = JSON.stringify(job);
            setPreviewDirty(false);
          }}
        />
        </div>
      </section>

      {/* Mobile bottom bar */}
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-sm border-t hairline px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] grid grid-cols-2 gap-2"
        data-testid="mobile-bottom-bar"
      >
        <button
          type="button"
          data-testid="mobile-preview-btn"
          disabled={!parse}
          onClick={() => {
            if (previewDirty) requestPreview();
            setMobileView("preview");
          }}
          className="min-h-[48px] flex items-center justify-center gap-2 border border-neutral-900 text-neutral-900 text-sm font-medium rounded-sm disabled:opacity-30"
        >
          <Eye className="w-4 h-4" /> Preview
          {previewDirty && parse && <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />}
        </button>
        <button
          type="button"
          data-testid="mobile-generate-btn"
          disabled={!parse || generating || reviewPending}
          onClick={() => {
            generate();
            setMobileView("preview");
          }}
          className="min-h-[48px] flex items-center justify-center gap-2 bg-neutral-900 text-white text-sm font-medium rounded-sm disabled:opacity-40"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Generate PDF
        </button>
      </div>
    </div>
  );
}

/* -------------------------- Preview (native PDF via iframe) -------------------------- */

const PAGE_ASPECT = {
  A4: 210 / 297,
  Letter: 216 / 279,
  A3: 297 / 420,
  A5: 148 / 210,
};

function CatalogPreview({ items, job, presets, dirty, onPreviewLoaded, isMobile, title, onReset }) {
  const activePreset = presets.find((p) => p.id === job.preset_id) || null;
  const pageSize = job.overrides.page_size || activePreset?.page_size || "A4";
  const aspect = PAGE_ASPECT[pageSize] || PAGE_ASPECT.A4;

  const effectiveLayout =
    job.layout || activePreset?.default_layout || "editorial";

  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const lastUrlRef = useRef(null);

  // Expose a refresh() method to the parent via a ref.
  const refresh = async () => {
    if (!job.parse_id || !items?.length) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const res = await api.post("/preview-pdf", job, {
        responseType: "blob",
        signal: ctrl.signal,
      });
      const blob = res.data;
      const newUrl = URL.createObjectURL(blob);
      if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
      lastUrlRef.current = newUrl;
      setPdfUrl(newUrl);
      onPreviewLoaded?.();
    } catch (e) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      setError(e?.response?.data?.detail || e?.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  // Auto-preview on first load only (when items first arrive)
  useEffect(() => {
    if (items?.length && !pdfUrl && !loading) {
      refresh();
    }
  }, [items?.length]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
    };
  }, []);

  // Register refresh() to be triggered via a global event dispatched by the
  // parent's Preview button. Keeps the button state (dirty/loading) in the
  // parent but lets the child own the fetch.
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("catalog:preview-refresh", handler);
    return () => window.removeEventListener("catalog:preview-refresh", handler);
  }, [job]);

  if (!items?.length) {
    return (
      <div
        className="paper rounded-sm mx-auto w-full"
        style={{ aspectRatio: `${aspect}`, maxWidth: "min(72ch, 100%)" }}
        data-testid="catalog-preview-empty"
      >
        <div className="w-full h-full grid place-items-center text-center text-neutral-400">
          <div>
            <ImageIcon className="w-8 h-8 mx-auto mb-3" />
            <p className="text-sm">Upload an Excel file (or load the sample) to see the catalog preview.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full" style={{ maxWidth: isMobile ? "100%" : "min(680px, 100%)" }}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-[10px] uppercase tracking-widest text-neutral-500" data-testid="preview-header">
        <h1 className="font-serif-editorial text-xl normal-case tracking-normal text-neutral-900 truncate max-w-full" data-testid="preview-title">
          {title}
        </h1>
        <span>{items.length} items · {pageSize}</span>
        <span data-testid="preview-effective-layout">
          Layout · <span className="text-neutral-900">{effectiveLayout.replace("_", " ")}</span>
        </span>
        <span className="flex items-center gap-3 ml-auto">
          {dirty && !loading && (
            <span
              data-testid="preview-stale-badge"
              className="flex items-center gap-1.5"
              title="Preview is out of date"
            >
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
              <span className="text-amber-700">Changes pending</span>
            </span>
          )}
          {loading && (
            <span className="flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Rendering
            </span>
          )}
          {onReset && (
            <button
              data-testid="reset-builder-btn"
              type="button"
              onClick={onReset}
              title="Reset current catalog"
              className="uppercase tracking-widest text-neutral-500 hover:text-neutral-900 flex items-center gap-1 py-1"
            >
              <RefreshCw className="w-3 h-3" /> Reset
            </button>
          )}
        </span>
      </div>

      {error ? (
        <div className="border border-red-200 bg-red-50 rounded-sm p-4 text-xs text-red-700">
          Preview error: {String(error)}
        </div>
      ) : pdfUrl ? (
        <PdfPreview url={pdfUrl} height={isMobile ? null : "calc(100vh - 260px)"} />
      ) : (
        <div
          className="paper rounded-sm w-full grid place-items-center text-neutral-300"
          style={{ aspectRatio: `${aspect}` }}
        >
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}
    </div>
  );
}

function LayoutIcon({ code }) {
  const cellClass = "fill-current text-neutral-500";
  switch (code) {
    case "editorial":
      return (
        <svg viewBox="0 0 20 20" className="w-4 h-4">
          <rect x="1" y="1" width="18" height="8.5" className={cellClass} rx="0.5" />
          <rect x="1" y="10.5" width="18" height="8.5" className={cellClass} rx="0.5" />
        </svg>
      );
    case "photo_story":
      return (
        <svg viewBox="0 0 20 20" className="w-4 h-4">
          <rect x="1" y="1" width="18" height="18" className={cellClass} rx="0.5" />
        </svg>
      );
    case "compact":
      return (
        <svg viewBox="0 0 20 20" className="w-4 h-4">
          <rect x="1" y="1" width="8.5" height="8.5" className={cellClass} rx="0.5" />
          <rect x="10.5" y="1" width="8.5" height="8.5" className={cellClass} rx="0.5" />
          <rect x="1" y="10.5" width="8.5" height="8.5" className={cellClass} rx="0.5" />
          <rect x="10.5" y="10.5" width="8.5" height="8.5" className={cellClass} rx="0.5" />
        </svg>
      );
    case "grid6":
      return (
        <svg viewBox="0 0 20 20" className="w-4 h-4">
          {[0, 1].flatMap((c) =>
            [0, 1, 2].map((r) => (
              <rect
                key={`${c}-${r}`}
                x={1 + c * 9.5}
                y={1 + r * 6.33}
                width="8.5"
                height="5.66"
                className={cellClass}
                rx="0.5"
              />
            ))
          )}
        </svg>
      );
    case "data_table":
      return (
        <svg viewBox="0 0 20 20" className="w-4 h-4">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <rect
              key={i}
              x="1"
              y={1 + i * 2.35}
              width="18"
              height="1.85"
              className={cellClass}
              rx="0.2"
            />
          ))}
        </svg>
      );
    default:
      return null;
  }
}

/* -------------------------- primitives -------------------------- */
function Section({ title, subtitle, icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {icon && <span className="text-neutral-500">{icon}</span>}
        <div className="overline">{title}</div>
      </div>
      {subtitle && <div className="text-xs text-neutral-500 mb-4 -mt-1">{subtitle}</div>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-neutral-500 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-neutral-500 block mb-1.5">{label}</span>
      <div className="flex items-center gap-2 border hairline rounded-sm bg-white px-2 py-1.5">
        <input
          type="color"
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-6 h-6 rounded-sm border-0 bg-transparent cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 text-xs bg-transparent focus:outline-none"
        />
      </div>
    </label>
  );
}

function ToggleChip({ active, onClick, children, icon, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest rounded-sm border transition-colors ${
        active
          ? "bg-neutral-900 text-white border-neutral-900"
          : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

const ReviewChoice = ({ onFix, onContinue }) => (
  <div data-testid="review-choice" className="mt-3 pt-3 border-t border-[var(--brand-warn-border)] space-y-2">
    <div className="text-[11px]">
      Some rows have values that look wrong for their column. Fix the file, or continue with those values left blank.
    </div>
    <div className="flex gap-2">
      <button
        data-testid="review-fix-btn"
        type="button"
        onClick={onFix}
        className="flex-1 text-[10px] uppercase tracking-widest py-2 border border-neutral-900 rounded-sm bg-white text-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
      >
        Fix file &amp; re-upload
      </button>
      <button
        data-testid="review-continue-btn"
        type="button"
        onClick={onContinue}
        className="flex-1 text-[10px] uppercase tracking-widest py-2 rounded-sm bg-neutral-900 text-white hover:bg-black transition-colors"
      >
        Continue anyway
      </button>
    </div>
  </div>
);

const TileToggle = ({ testId, label, hint, checked, onChange }) => (
  <label className="flex items-start justify-between gap-3 cursor-pointer">
    <span className="min-w-0">
      <span className="block text-xs font-medium uppercase tracking-widest">{label}</span>
      <span className="block text-[11px] text-neutral-500 mt-0.5 leading-relaxed">{hint}</span>
    </span>
    <Switch data-testid={testId} checked={checked} onCheckedChange={onChange} className="mt-0.5 shrink-0" />
  </label>
);

const LayoutTile = ({ testid, active, label, icon, onClick }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`flex flex-col items-center justify-center gap-1.5 px-2 py-3 min-h-[64px] rounded-sm border text-center transition-colors ${
      active ? "border-neutral-900 bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"
    }`}
  >
    <span className="w-5 h-5 flex items-center justify-center">{icon}</span>
    <span className="text-[10px] font-medium uppercase tracking-widest leading-tight">{label}</span>
  </button>
);

const ChoiceBtn = ({ testid, active, onClick, children }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`flex-1 sm:flex-none min-w-[44px] px-3 py-2 text-xs uppercase tracking-widest rounded-sm border transition-colors ${
      active
        ? "bg-neutral-900 text-white border-neutral-900"
        : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-400"
    }`}
  >
    {children}
  </button>
);

const OutputStrip = ({
  lastJob, lastMediaJob, mediaGenerating, mediaStatus, mediaStageLabel, mediaDisabled, onMedia, mobileProgress,
}) => (
  <div data-testid="output-strip" className="mb-4 space-y-2">
    {mobileProgress}
    <div className="border hairline bg-white rounded-sm px-4 py-3 flex flex-wrap items-center gap-3 text-xs">
      <span className="overline mr-1">Output</span>
      {lastJob ? (
        <div className="flex items-center gap-3 min-w-0 basis-full sm:basis-auto flex-1" data-testid="last-job-card">
          <div className="min-w-0">
            <div className="font-medium truncate">{lastJob.filename}</div>
            <div className="text-neutral-500 text-[11px]">Expires {new Date(lastJob.expires_at).toLocaleDateString()}</div>
          </div>
          <a
            data-testid="download-pdf-link"
            href={fileUrl(lastJob.pdf_file_id)}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 flex items-center gap-1 uppercase tracking-widest text-[10px] px-3 py-2 min-h-[44px] sm:min-h-[36px] border border-neutral-900 rounded-sm hover:bg-neutral-900 hover:text-white transition-colors"
          >
            <Download className="w-3 h-3" /> PDF
          </a>
        </div>
      ) : (
        <span className="text-neutral-400 flex-1">Generate a PDF to download it here</span>
      )}
      <button
        data-testid="download-media-btn"
        type="button"
        disabled={mediaDisabled}
        onClick={onMedia}
        className="shrink-0 flex items-center justify-center gap-2 border border-neutral-900 text-neutral-900 text-[10px] uppercase tracking-widest px-3 py-2 min-h-[44px] sm:min-h-[36px] rounded-sm disabled:opacity-30 hover:bg-neutral-900 hover:text-white transition-all"
      >
        {mediaGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderDown className="w-3.5 h-3.5" />}
        Media (ZIP)
      </button>
      {lastMediaJob && !mediaGenerating && (
        <div data-testid="last-media-job-card" className="w-full flex items-center justify-between gap-3 pt-2 border-t hairline text-[11px]">
          <div className="min-w-0">
            <div className="truncate text-neutral-700">{lastMediaJob.filename}</div>
            {lastMediaJob.missing_count > 0 && (
              <div className="text-amber-600" data-testid="media-missing-count">
                {lastMediaJob.missing_count} skipped — see missing.txt
              </div>
            )}
          </div>
          <a
            data-testid="download-media-link"
            href={fileUrl(lastMediaJob.zip_file_id)}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 flex items-center gap-1 uppercase tracking-widest text-[10px] px-3 py-2 min-h-[44px] sm:min-h-[36px] border border-neutral-900 rounded-sm hover:bg-neutral-900 hover:text-white transition-colors"
          >
            <Download className="w-3 h-3" /> ZIP
          </a>
        </div>
      )}
    </div>
    {mediaGenerating && mediaStatus && (
      <div data-testid="media-progress" className="border hairline bg-white rounded-sm px-4 py-2 text-[11px] space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-neutral-600">{mediaStageLabel(mediaStatus)}</span>
          <span data-testid="media-progress-pct" className="tabular-nums text-neutral-500">
            {mediaStatus.progress_pct ?? 0}%
          </span>
        </div>
        <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
          <div className="h-full bg-neutral-900 transition-[width] duration-500" style={{ width: `${mediaStatus.progress_pct ?? 0}%` }} />
        </div>
      </div>
    )}
  </div>
);
